import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../../lib/apiClient";
import logger from "../../../utils/logger";
import { useAuth } from "../../../contexts/AuthContext";

export interface ChatMessage {
  id: string;
  type: "user" | "agent";
  content: string;
  timestamp: Date;
  suggestedWords?: string[];
}

interface UseChatLogicParams {
  targetLanguage: string;
  nativeLanguage: string;
  initialSelectedWords?: string[];
}

export const useChatLogic = ({
  targetLanguage,
  nativeLanguage,
  initialSelectedWords = [],
}: UseChatLogicParams) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [selectedWords, setSelectedWords] =
    useState<string[]>(initialSelectedWords);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [chatPhase, setChatPhase] = useState<
    "intro" | "collecting" | "expanding" | "complete"
  >("intro");
  const [processedTopics, setProcessedTopics] = useState<Set<string>>(
    new Set()
  );
  const [usedWords, setUsedWords] = useState<Set<string>>(
    new Set(initialSelectedWords)
  );

  // Add language validation to catch incorrect settings
  console.log("ChatLogic - Target language:", targetLanguage);
  console.log("ChatLogic - Native language:", nativeLanguage);

  // Validate that we're using supported languages
  if (
    !["english", "dutch", "hebrew", "spanish", "french", "german"].includes(
      targetLanguage
    )
  ) {
    console.warn(
      `Unsupported target language: ${targetLanguage}, falling back to dutch`
    );
  }

  if (!["english", "dutch", "hebrew"].includes(nativeLanguage)) {
    console.warn(
      `Unsupported native language: ${nativeLanguage}, falling back to english`
    );
  }

  const getLocalizedWelcomeMessage = useCallback(() => {
    // Get the display name for the target language
    const targetLanguageDisplayNames = {
      english: { english: "English", dutch: "Engels", hebrew: "אנגלית" },
      dutch: { english: "Dutch", dutch: "Nederlands", hebrew: "הולנדית" },
      hebrew: { english: "Hebrew", dutch: "Hebreeuws", hebrew: "עברית" },
      spanish: { english: "Spanish", dutch: "Spaans", hebrew: "ספרדית" },
      french: { english: "French", dutch: "Frans", hebrew: "צרפתית" },
      german: { english: "German", dutch: "Duits", hebrew: "גרמנית" },
    };

    const targetLangDisplay =
      targetLanguageDisplayNames[
        targetLanguage as keyof typeof targetLanguageDisplayNames
      ]?.[nativeLanguage as keyof typeof targetLanguageDisplayNames.english] ||
      targetLanguage;

    const messages = {
      english: `Hello! I'm your vocabulary assistant. I'll help you build a personalized word collection for learning ${targetLangDisplay}.

Let's start by telling me some words you already know in ${targetLangDisplay}, or topics you want to learn about. For example, you can say "I know colors" or "I want to learn about food" or just mention some words you know.

What would you like to start with?`,
      dutch: `Hallo! Ik ben je woordenschat assistent. Ik help je bij het opbouwen van een gepersonaliseerde woordcollectie voor het leren van ${targetLangDisplay}.

Laten we beginnen door me enkele woorden te vertellen die je al kent in het ${targetLangDisplay}, of onderwerpen waarover je wilt leren. Je kunt bijvoorbeeld zeggen "Ik ken kleuren" of "Ik wil leren over eten" of gewoon enkele woorden opnoemen die je kent.

Waar wil je mee beginnen?`,
      hebrew: `שלום! אני עוזר אוצר המילים שלך. אני אעזור לך לבנות אוסף מילים מותאם אישית ללימוד ${targetLangDisplay}.

בואו נתחיל בכך שתספר לי כמה מילים שאתה כבר יודע ב${targetLangDisplay}, או נושאים שאתה רוצה ללמוד עליהם. לדוגמה, אתה יכול לומר "אני יודע צבעים" או "אני רוצה ללמוד על אוכל" או פשוט להזכיר כמה מילים שאתה יודע.

במה תרצה להתחיל?`,
    };
    return (
      messages[nativeLanguage as keyof typeof messages] || messages.english
    );
  }, [nativeLanguage, targetLanguage]);

  const initializeChat = useCallback(() => {
    const welcomeMessage: ChatMessage = {
      id: "1",
      type: "agent",
      content: getLocalizedWelcomeMessage(),
      timestamp: new Date(),
    };

    setMessages([welcomeMessage]);
  }, [getLocalizedWelcomeMessage]);

  useEffect(() => {
    initializeChat();
  }, [initializeChat]);

  const sendMessage = async () => {
    if (!currentMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: "user",
      content: currentMessage,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setCurrentMessage("");
    setIsAgentTyping(true);

    // Simulate agent processing and response
    setTimeout(async () => {
      await processUserInput(currentMessage);
      setIsAgentTyping(false);
    }, 1500);
  };

  const processUserInput = async (input: string) => {
    try {
      // Here we would normally call an AI service to process the input
      // For now, we'll simulate intelligent responses
      const response = await generateAgentResponse(input);

      const agentMessage: ChatMessage = {
        id: Date.now().toString(),
        type: "agent",
        content: response.content,
        timestamp: new Date(),
        suggestedWords: response.suggestedWords,
      };

      setMessages((prev) => [...prev, agentMessage]);

      // Stop the thinking indicator as soon as we have a response
      setIsAgentTyping(false);

      // Add suggested words to the collection
      if (response.suggestedWords && response.suggestedWords.length > 0) {
        console.log(
          `Processing ${response.suggestedWords.length} suggested words from AI response`
        );
        const newWords = response.suggestedWords.filter(
          (word) => !selectedWords.includes(word)
        );

        if (newWords.length > 0) {
          console.log(
            `Adding ${
              newWords.length
            } new words to selected words: ${newWords.join(", ")}`
          );
          setSelectedWords((prev) => [...new Set([...prev, ...newWords])]);

          // Save words to database and generate sentences asynchronously
          if (user) {
            // Run this in the background without blocking
            setTimeout(async () => {
              try {
                for (const word of newWords) {
                  try {
                    console.log(`Saving vocabulary word "${word}" to database`);
                    await apiClient.saveWordToCollection(
                      word,
                      targetLanguage.toLowerCase(),
                      nativeLanguage.toLowerCase()
                    );
                    console.log(
                      `Successfully processed vocabulary word "${word}"`
                    );
                  } catch (wordError) {
                    console.error(
                      `Error processing word "${word}":`,
                      wordError
                    );
                  }
                }
              } catch (error) {
                console.error(
                  "Error in background vocabulary word processing:",
                  error
                );
              }
            }, 100); // Small delay to ensure UI updates first
          }
        } else {
          console.log(
            "No new words to add - all suggested words are already in the collection"
          );
        }
      } else {
        console.log("No words suggested in this AI response");
      }
    } catch (error) {
      logger.error("Failed to process user input:", error);
      setIsAgentTyping(false);

      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: "agent",
        content:
          "I'm sorry, I had trouble processing that. Could you try rephrasing or tell me about a different topic?",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    }
  };



  // Add word to the collection when clicked in the UI
  const addWordToCollection = async (word: string) => {
    if (!selectedWords.includes(word)) {
      setSelectedWords((prev) => [...prev, word]);

      const confirmMessage: ChatMessage = {
        id: Date.now().toString(),
        type: "agent",
        content: `Added "${word}" to your collection! 👍`,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, confirmMessage]);

      // Save word to database and generate sentences asynchronously
      if (user) {
        // Run this in the background without blocking the UI
        setTimeout(async () => {
          try {
            console.log(`Saving manually added word "${word}" to database`);
            await apiClient.saveWordToCollection(
              word,
              targetLanguage.toLowerCase(),
              nativeLanguage.toLowerCase()
            );
            console.log(`Successfully processed manually added word "${word}"`);
          } catch (error) {
            console.error(
              "Error in background manually added word processing:",
              error
            );
          }
        }, 100); // Small delay to ensure UI updates first
      }
    }
  };

  const getLocalizedResponse = (type: string, targetLangDisplay: string) => {
    const responses = {
      english: {
        war: `War and military vocabulary is often found in news and history. I've added some essential military terms to your collection:`,
        politics: `Perfect! Political vocabulary is important for discussions and understanding news. I've added some essential political terms to your collection:`,
        colors: `Great! I see you're interested in colors. I've added some basic color words to your vocabulary. Here are some related words that might also be useful:`,
        food: `Perfect! Food vocabulary is very practical. I've added some essential food-related words. These will help you in restaurants and when shopping:`,
        family: `Family words are so important! I've added important family member terms. These are words you'll often use when talking about yourself:`,
        travel: `Travel vocabulary is essential! I've added some important travel terms that will help you navigate and communicate while traveling:`,
        work: `Professional vocabulary is very useful! I've added workplace terms that will help you in business situations:`,
        general: `I've added the words you mentioned to your collection. Based on these, here are some related words that might be helpful:`,
        unknown: `I understand you want to learn more vocabulary. Could you tell me about a specific topic you're interested in? For example: "I want to learn about sports" or "I need words for shopping" or just mention some words you already know.`,
        progress15: `\n\nWhat other topics interest you? We're building a great foundation - let's add a few more areas!`,
        progress25: `\n\nExcellent progress! Any other specific situations where you'd like to use ${targetLangDisplay}?`,
        complete: `\n\nWonderful! You now have a solid vocabulary foundation. Would you like to add anything else, or shall we move on to the next step?`,
      },
      dutch: {
        war: `Oorlogs- en militaire woordenschat komt vaak voor in nieuws en geschiedenis. Ik heb enkele essentiële militaire termen toegevoegd aan je collectie:`,
        politics: `Perfect! Politieke woordenschat is belangrijk voor discussies en het begrijpen van nieuws. Ik heb enkele essentiële politieke termen toegevoegd aan je collectie:`,
        colors: `Geweldig! Ik zie dat je geïnteresseerd bent in kleuren. Ik heb enkele basiskleurwoorden toegevoegd aan je woordenschat. Hier zijn enkele gerelateerde woorden die ook nuttig kunnen zijn:`,
        food: `Perfect! Voedselwoordenschat is zeer praktisch. Ik heb enkele essentiële voedselgerelateerde woorden toegevoegd. Deze helpen je in restaurants en bij het boodschappen doen:`,
        family: `Familiewoorden zijn zo belangrijk! Ik heb belangrijke familielid termen toegevoegd. Dit zijn woorden die je vaak zult gebruiken als je over jezelf praat:`,
        travel: `Reiswoordenschat is essentieel! Ik heb enkele belangrijke reistermen toegevoegd die je helpen bij het navigeren en communiceren tijdens het reizen:`,
        work: `Professionele woordenschat is zeer nuttig! Ik heb werkplektermen toegevoegd die je helpen in zakelijke situaties:`,
        general: `Ik heb de woorden die je noemde toegevoegd aan je collectie. Op basis hiervan zijn hier enkele gerelateerde woorden die nuttig kunnen zijn:`,
        unknown: `Ik begrijp dat je meer woordenschat wilt leren. Kun je me vertellen over een specifiek onderwerp waarin je geïnteresseerd bent? Bijvoorbeeld: "Ik wil leren over sport" of "Ik heb woorden nodig voor het winkelen" of noem gewoon enkele woorden die je al kent.`,
        progress15: `\n\nWelke andere onderwerpen interesseren je? We bouwen een geweldige basis - laten we nog een paar gebieden toevoegen!`,
        progress25: `\n\nUitstekende vooruitgang! Zijn er andere specifieke situaties waarin je ${targetLangDisplay} wilt gebruiken?`,
        complete: `\n\nPrachtig! Je hebt nu een solide woordenschatbasis. Wil je nog iets toevoegen, of gaan we door naar de volgende stap?`,
      },
      hebrew: {
        war: `אוצר מילים צבאי ומלחמתי נמצא לעתים קרובות בחדשות ובהיסטוריה. הוספתי כמה מונחים צבאיים חיוניים לאוסף שלך:`,
        politics: `מושלם! אוצר מילים פוליטי חשוב לדיונים והבנת חדשות. הוספתי כמה מונחים פוליטיים חיוניים לאוסף שלך:`,
        colors: `נהדר! אני רואה שאתה מתעניין בצבעים. הוספתי כמה מילות צבע בסיסיות לאוצר המילים שלך. הנה כמה מילים קשורות שעלולות להיות שימושיות גם:`,
        food: `מושלם! אוצר מילים של אוכל הוא מאוד מעשי. הוספתי כמה מילים חיוניות הקשורות לאוכל. אלה יעזרו לך במסעדות ובקניות:`,
        family: `מילות משפחה כל כך חשובות! הוספתי מונחי בני משפחה חשובים. אלה מילים שתשתמש בהן לעתים קרובות כשאתה מדבר על עצמך:`,
        travel: `אוצר מילים לנסיעות הוא חיוני! הוספתי כמה מונחי נסיעה חשובים שיעזרו לך לנווט ולתקשר בזמן נסיעה:`,
        work: `אוצר מילים מקצועי מאוד שימושי! הוספתי מונחי מקום עבודה שיעזרו לך במצבים עסקיים:`,
        general: `הוספתי את המילים שהזכרת לאוסף שלך. בהתבסס על אלה, הנה כמה מילים קשורות שעלולות להיות מועילות:`,
        unknown: `אני מבין שאתה רוצה ללמוד עוד אוצר מילים. האם תוכל לספר לי על נושא ספציפי שמעניין אותך? לדוגמה: "אני רוצה ללמוד על ספורט" או "אני צריך מילים לקניות" או פשוט תזכיר כמה מילים שאתה כבר יודע.`,
        progress15: `\n\nאילו נושאים אחרים מעניינים אותך? אנחנו בונים בסיס נהדר - בואו נוסיף עוד כמה תחומים!`,
        progress25: `\n\nהתקדמות מעולה! יש עוד מצבים ספציפיים שבהם תרצה להשתמש ב${targetLangDisplay}?`,
        complete: `\n\nנפלא! עכשיו יש לך בסיס אוצר מילים יציב. האם תרצה להוסיף עוד משהו, או שנמשיך לשלב הבא?`,
      },
    };

    return (
      responses[nativeLanguage as keyof typeof responses] || responses.english
    );
  };

  const generateAgentResponse = async (
    input: string
  ): Promise<{ content: string; suggestedWords?: string[] }> => {
    // Simulate AI processing - in real implementation, this would call the sentence-generator function
    const lowerInput = input.toLowerCase();
    console.log(`Processing user input: "${input}"`);

    // Get the display name for the target language
    const targetLanguageDisplayNames = {
      english: { english: "English", dutch: "Engels", hebrew: "אנגלית" },
      dutch: { english: "Dutch", dutch: "Nederlands", hebrew: "הולנדית" },
      hebrew: { english: "Hebrew", dutch: "Hebreeuws", hebrew: "עברית" },
      spanish: { english: "Spanish", dutch: "Spaans", hebrew: "ספרדית" },
      french: { english: "French", dutch: "Frans", hebrew: "צרפתית" },
      german: { english: "German", dutch: "Duits", hebrew: "גרמנית" },
    };

    const targetLangDisplay =
      targetLanguageDisplayNames[
        targetLanguage as keyof typeof targetLanguageDisplayNames
      ]?.[nativeLanguage as keyof typeof targetLanguageDisplayNames.english] ||
      targetLanguage;

    // Extended vocabulary sets for different languages and different subtopics
    const vocabularyMap: Record<
      string,
      Record<string, Record<string, string[]>>
    > = {
      war: {
        basic: {
          english: [
            "war",
            "soldier",
            "army",
            "battle",
            "peace",
            "weapon",
            "military",
            "defense",
          ],
          dutch: [
            "oorlog",
            "soldaat",
            "leger",
            "gevecht",
            "vrede",
            "wapen",
            "militair",
            "verdediging",
          ],
          hebrew: [
            "מלחמה",
            "חייל",
            "צבא",
            "קרב",
            "שלום",
            "נשק",
            "צבאי",
            "הגנה",
          ],
          spanish: [
            "guerra",
            "soldado",
            "ejército",
            "batalla",
            "paz",
            "arma",
            "militar",
            "defensa",
          ],
          french: [
            "guerre",
            "soldat",
            "armée",
            "bataille",
            "paix",
            "arme",
            "militaire",
            "défense",
          ],
          german: [
            "Krieg",
            "Soldat",
            "Armee",
            "Schlacht",
            "Frieden",
            "Waffe",
            "militärisch",
            "Verteidigung",
          ],
        },
        equipment: {
          english: [
            "tank",
            "aircraft",
            "missile",
            "bomb",
            "rifle",
            "submarine",
            "destroyer",
            "shield",
          ],
          dutch: [
            "tank",
            "vliegtuig",
            "raket",
            "bom",
            "geweer",
            "onderzeeër",
            "torpedojager",
            "schild",
          ],
          hebrew: [
            "טנק",
            "מטוס",
            "טיל",
            "פצצה",
            "רובה",
            "צוללת",
            "משחתת",
            "מגן",
          ],
          spanish: [
            "tanque",
            "avión",
            "misil",
            "bomba",
            "rifle",
            "submarino",
            "destructor",
            "escudo",
          ],
          french: [
            "char",
            "avion",
            "missile",
            "bombe",
            "fusil",
            "sous-marin",
            "destroyer",
            "bouclier",
          ],
          german: [
            "Panzer",
            "Flugzeug",
            "Rakete",
            "Bombe",
            "Gewehr",
            "U-Boot",
            "Zerstörer",
            "Schild",
          ],
        },
        strategy: {
          english: [
            "strategy",
            "attack",
            "retreat",
            "surrender",
            "victory",
            "defeat",
            "ally",
            "enemy",
          ],
          dutch: [
            "strategie",
            "aanval",
            "terugtrekking",
            "overgave",
            "overwinning",
            "nederlaag",
            "bondgenoot",
            "vijand",
          ],
          hebrew: [
            "אסטרטגיה",
            "התקפה",
            "נסיגה",
            "כניעה",
            "ניצחון",
            "תבוסה",
            "בעל ברית",
            "אויב",
          ],
          spanish: [
            "estrategia",
            "ataque",
            "retirada",
            "rendición",
            "victoria",
            "derrota",
            "aliado",
            "enemigo",
          ],
          french: [
            "stratégie",
            "attaque",
            "retraite",
            "reddition",
            "victoire",
            "défaite",
            "allié",
            "ennemi",
          ],
          german: [
            "Strategie",
            "Angriff",
            "Rückzug",
            "Kapitulation",
            "Sieg",
            "Niederlage",
            "Verbündeter",
            "Feind",
          ],
        },
      },
      politics: {
        basic: {
          english: [
            "government",
            "election",
            "president",
            "vote",
            "parliament",
            "democracy",
            "politician",
            "party",
          ],
          dutch: [
            "regering",
            "verkiezing",
            "president",
            "stem",
            "parlement",
            "democratie",
            "politicus",
            "partij",
          ],
          hebrew: [
            "ממשלה",
            "בחירות",
            "נשיא",
            "הצבעה",
            "פרלמנט",
            "דמוקרטיה",
            "פוליטיקאי",
            "מפלגה",
          ],
          spanish: [
            "gobierno",
            "elección",
            "presidente",
            "voto",
            "parlamento",
            "democracia",
            "político",
            "partido",
          ],
          french: [
            "gouvernement",
            "élection",
            "président",
            "vote",
            "parlement",
            "démocratie",
            "politicien",
            "parti",
          ],
          german: [
            "Regierung",
            "Wahl",
            "Präsident",
            "Stimme",
            "Parlament",
            "Demokratie",
            "Politiker",
            "Partei",
          ],
        },
        debate: {
          english: [
            "debate",
            "policy",
            "campaign",
            "speech",
            "opinion",
            "controversy",
            "ideology",
            "reform",
          ],
          dutch: [
            "debat",
            "beleid",
            "campagne",
            "toespraak",
            "mening",
            "controverse",
            "ideologie",
            "hervorming",
          ],
          hebrew: [
            "דיון",
            "מדיניות",
            "קמפיין",
            "נאום",
            "דעה",
            "מחלוקת",
            "אידאולוגיה",
            "רפורמה",
          ],
          spanish: [
            "debate",
            "política",
            "campaña",
            "discurso",
            "opinión",
            "controversia",
            "ideología",
            "reforma",
          ],
          french: [
            "débat",
            "politique",
            "campagne",
            "discours",
            "opinion",
            "controverse",
            "idéologie",
            "réforme",
          ],
          german: [
            "Debatte",
            "Politik",
            "Kampagne",
            "Rede",
            "Meinung",
            "Kontroverse",
            "Ideologie",
            "Reform",
          ],
        },
        international: {
          english: [
            "diplomacy",
            "treaty",
            "ambassador",
            "summit",
            "foreign",
            "alliance",
            "sanctions",
            "embassy",
          ],
          dutch: [
            "diplomatie",
            "verdrag",
            "ambassadeur",
            "top",
            "buitenlands",
            "alliantie",
            "sancties",
            "ambassade",
          ],
          hebrew: [
            "דיפלומטיה",
            "אמנה",
            "שגריר",
            "פסגה",
            "חוץ",
            "ברית",
            "סנקציות",
            "שגרירות",
          ],
          spanish: [
            "diplomacia",
            "tratado",
            "embajador",
            "cumbre",
            "extranjero",
            "alianza",
            "sanciones",
            "embajada",
          ],
          french: [
            "diplomatie",
            "traité",
            "ambassadeur",
            "sommet",
            "étranger",
            "alliance",
            "sanctions",
            "ambassade",
          ],
          german: [
            "Diplomatie",
            "Vertrag",
            "Botschafter",
            "Gipfel",
            "ausländisch",
            "Bündnis",
            "Sanktionen",
            "Botschaft",
          ],
        },
      },
      colors: {
        basic: {
          english: [
            "red",
            "blue",
            "green",
            "yellow",
            "black",
            "white",
            "orange",
            "purple",
          ],
          dutch: [
            "rood",
            "blauw",
            "groen",
            "geel",
            "zwart",
            "wit",
            "oranje",
            "paars",
          ],
          hebrew: [
            "אדום",
            "כחול",
            "ירוק",
            "צהוב",
            "שחור",
            "לבן",
            "כתום",
            "סגול",
          ],
          spanish: [
            "rojo",
            "azul",
            "verde",
            "amarillo",
            "negro",
            "blanco",
            "naranja",
            "morado",
          ],
          french: [
            "rouge",
            "bleu",
            "vert",
            "jaune",
            "noir",
            "blanc",
            "orange",
            "violet",
          ],
          german: [
            "rot",
            "blau",
            "grün",
            "gelb",
            "schwarz",
            "weiß",
            "orange",
            "lila",
          ],
        },
        advanced: {
          english: [
            "teal",
            "maroon",
            "navy",
            "olive",
            "burgundy",
            "indigo",
            "lavender",
            "turquoise",
          ],
          dutch: [
            "groenblauw",
            "kastanjebruin",
            "marineblauw",
            "olijfgroen",
            "bordeauxrood",
            "indigo",
            "lavendel",
            "turkoois",
          ],
          hebrew: [
            "טורקיז",
            "חום כהה",
            "כחול כהה",
            "זית",
            "בורדו",
            "אינדיגו",
            "לבנדר",
            "טורקיז",
          ],
          spanish: [
            "verde azulado",
            "granate",
            "azul marino",
            "verde oliva",
            "burdeos",
            "índigo",
            "lavanda",
            "turquesa",
          ],
          french: [
            "bleu sarcelle",
            "marron",
            "bleu marine",
            "olive",
            "bordeaux",
            "indigo",
            "lavande",
            "turquoise",
          ],
          german: [
            "petrol",
            "kastanienbraun",
            "marineblau",
            "olivgrün",
            "burgunderrot",
            "indigo",
            "lavendel",
            "türkis",
          ],
        },
      },
      food: {
        basic: {
          english: [
            "bread",
            "water",
            "coffee",
            "apple",
            "chicken",
            "rice",
            "vegetables",
            "delicious",
          ],
          dutch: [
            "brood",
            "water",
            "koffie",
            "appel",
            "kip",
            "rijst",
            "groenten",
            "lekker",
          ],
          hebrew: ["לחם", "מים", "קפה", "תפוח", "עוף", "אורז", "ירקות", "טעים"],
          spanish: [
            "pan",
            "agua",
            "café",
            "manzana",
            "pollo",
            "arroz",
            "verduras",
            "delicioso",
          ],
          french: [
            "pain",
            "eau",
            "café",
            "pomme",
            "poulet",
            "riz",
            "légumes",
            "délicieux",
          ],
          german: [
            "Brot",
            "Wasser",
            "Kaffee",
            "Apfel",
            "Hühnchen",
            "Reis",
            "Gemüse",
            "lecker",
          ],
        },
        restaurant: {
          english: [
            "menu",
            "waiter",
            "dessert",
            "bill",
            "reservation",
            "spicy",
            "sweet",
            "chef",
          ],
          dutch: [
            "menu",
            "ober",
            "nagerecht",
            "rekening",
            "reservering",
            "pittig",
            "zoet",
            "chef",
          ],
          hebrew: [
            "תפריט",
            "מלצר",
            "קינוח",
            "חשבון",
            "הזמנה",
            "חריף",
            "מתוק",
            "שף",
          ],
          spanish: [
            "menú",
            "camarero",
            "postre",
            "cuenta",
            "reserva",
            "picante",
            "dulce",
            "chef",
          ],
          french: [
            "menu",
            "serveur",
            "dessert",
            "addition",
            "réservation",
            "épicé",
            "sucré",
            "chef",
          ],
          german: [
            "Speisekarte",
            "Kellner",
            "Nachspeise",
            "Rechnung",
            "Reservierung",
            "scharf",
            "süß",
            "Koch",
          ],
        },
        cooking: {
          english: [
            "recipe",
            "ingredient",
            "bake",
            "fry",
            "boil",
            "taste",
            "kitchen",
            "meal",
          ],
          dutch: [
            "recept",
            "ingrediënt",
            "bakken",
            "braden",
            "koken",
            "proeven",
            "keuken",
            "maaltijd",
          ],
          hebrew: [
            "מתכון",
            "מרכיב",
            "לאפות",
            "לטגן",
            "להרתיח",
            "לטעום",
            "מטבח",
            "ארוחה",
          ],
          spanish: [
            "receta",
            "ingrediente",
            "hornear",
            "freír",
            "hervir",
            "probar",
            "cocina",
            "comida",
          ],
          french: [
            "recette",
            "ingrédient",
            "cuire",
            "frire",
            "bouillir",
            "goûter",
            "cuisine",
            "repas",
          ],
          german: [
            "Rezept",
            "Zutat",
            "backen",
            "braten",
            "kochen",
            "schmecken",
            "Küche",
            "Mahlzeit",
          ],
        },
      },
      family: {
        basic: {
          english: [
            "mother",
            "father",
            "sister",
            "brother",
            "grandmother",
            "grandfather",
            "child",
            "family",
          ],
          dutch: [
            "moeder",
            "vader",
            "zus",
            "broer",
            "grootmoeder",
            "grootvader",
            "kind",
            "familie",
          ],
          hebrew: ["אמא", "אבא", "אחות", "אח", "סבתא", "סבא", "ילד", "משפחה"],
          spanish: [
            "madre",
            "padre",
            "hermana",
            "hermano",
            "abuela",
            "abuelo",
            "niño",
            "familia",
          ],
          french: [
            "mère",
            "père",
            "sœur",
            "frère",
            "grand-mère",
            "grand-père",
            "enfant",
            "famille",
          ],
          german: [
            "Mutter",
            "Vater",
            "Schwester",
            "Bruder",
            "Großmutter",
            "Großvater",
            "Kind",
            "Familie",
          ],
        },
        extended: {
          english: [
            "uncle",
            "aunt",
            "cousin",
            "niece",
            "nephew",
            "parent",
            "sibling",
            "relative",
          ],
          dutch: [
            "oom",
            "tante",
            "neef/nicht",
            "nicht",
            "neefje",
            "ouder",
            "broer/zus",
            "familielid",
          ],
          hebrew: [
            "דוד",
            "דודה",
            "בן דוד",
            "אחיינית",
            "אחיין",
            "הורה",
            "אח/אחות",
            "קרוב משפחה",
          ],
          spanish: [
            "tío",
            "tía",
            "primo",
            "sobrina",
            "sobrino",
            "padre/madre",
            "hermano/a",
            "pariente",
          ],
          french: [
            "oncle",
            "tante",
            "cousin/e",
            "nièce",
            "neveu",
            "parent",
            "frère/sœur",
            "parent",
          ],
          german: [
            "Onkel",
            "Tante",
            "Cousin/e",
            "Nichte",
            "Neffe",
            "Elternteil",
            "Geschwister",
            "Verwandter",
          ],
        },
        relationships: {
          english: [
            "husband",
            "wife",
            "married",
            "engaged",
            "divorced",
            "widow",
            "wedding",
            "anniversary",
          ],
          dutch: [
            "man",
            "vrouw",
            "getrouwd",
            "verloofd",
            "gescheiden",
            "weduwe",
            "bruiloft",
            "jubileum",
          ],
          hebrew: [
            "בעל",
            "אישה",
            "נשוי",
            "מאורס",
            "גרוש",
            "אלמנה",
            "חתונה",
            "יום נישואין",
          ],
          spanish: [
            "marido",
            "esposa",
            "casado",
            "comprometido",
            "divorciado",
            "viuda",
            "boda",
            "aniversario",
          ],
          french: [
            "mari",
            "femme",
            "marié",
            "fiancé",
            "divorcé",
            "veuve",
            "mariage",
            "anniversaire",
          ],
          german: [
            "Ehemann",
            "Ehefrau",
            "verheiratet",
            "verlobt",
            "geschieden",
            "Witwe",
            "Hochzeit",
            "Jahrestag",
          ],
        },
      },
      travel: {
        transportation: {
          english: [
            "airplane",
            "train",
            "bus",
            "taxi",
            "subway",
            "ticket",
            "departure",
            "arrival",
          ],
          dutch: [
            "vliegtuig",
            "trein",
            "bus",
            "taxi",
            "metro",
            "kaartje",
            "vertrek",
            "aankomst",
          ],
          hebrew: [
            "מטוס",
            "רכבת",
            "אוטובוס",
            "מונית",
            "רכבת תחתית",
            "כרטיס",
            "יציאה",
            "הגעה",
          ],
          spanish: [
            "avión",
            "tren",
            "autobús",
            "taxi",
            "metro",
            "billete",
            "salida",
            "llegada",
          ],
          french: [
            "avion",
            "train",
            "bus",
            "taxi",
            "métro",
            "billet",
            "départ",
            "arrivée",
          ],
          german: [
            "Flugzeug",
            "Zug",
            "Bus",
            "Taxi",
            "U-Bahn",
            "Fahrkarte",
            "Abfahrt",
            "Ankunft",
          ],
        },
        accommodation: {
          english: [
            "hotel",
            "room",
            "reservation",
            "check-in",
            "check-out",
            "key",
            "reception",
            "stay",
          ],
          dutch: [
            "hotel",
            "kamer",
            "reservering",
            "inchecken",
            "uitchecken",
            "sleutel",
            "receptie",
            "verblijf",
          ],
          hebrew: [
            "מלון",
            "חדר",
            "הזמנה",
            "צ'ק-אין",
            "צ'ק-אאוט",
            "מפתח",
            "קבלה",
            "שהייה",
          ],
          spanish: [
            "hotel",
            "habitación",
            "reserva",
            "check-in",
            "check-out",
            "llave",
            "recepción",
            "estancia",
          ],
          french: [
            "hôtel",
            "chambre",
            "réservation",
            "enregistrement",
            "départ",
            "clé",
            "réception",
            "séjour",
          ],
          german: [
            "Hotel",
            "Zimmer",
            "Reservierung",
            "Check-in",
            "Check-out",
            "Schlüssel",
            "Rezeption",
            "Aufenthalt",
          ],
        },
        sightseeing: {
          english: [
            "museum",
            "monument",
            "tour",
            "guide",
            "tourist",
            "photo",
            "souvenir",
            "map",
          ],
          dutch: [
            "museum",
            "monument",
            "tour",
            "gids",
            "toerist",
            "foto",
            "souvenir",
            "kaart",
          ],
          hebrew: [
            "מוזיאון",
            "אנדרטה",
            "סיור",
            "מדריך",
            "תייר",
            "תמונה",
            "מזכרת",
            "מפה",
          ],
          spanish: [
            "museo",
            "monumento",
            "tour",
            "guía",
            "turista",
            "foto",
            "recuerdo",
            "mapa",
          ],
          french: [
            "musée",
            "monument",
            "visite",
            "guide",
            "touriste",
            "photo",
            "souvenir",
            "carte",
          ],
          german: [
            "Museum",
            "Denkmal",
            "Tour",
            "Führer",
            "Tourist",
            "Foto",
            "Souvenir",
            "Karte",
          ],
        },
      },
      work: {
        office: {
          english: [
            "meeting",
            "colleague",
            "boss",
            "project",
            "email",
            "deadline",
            "report",
            "office",
          ],
          dutch: [
            "vergadering",
            "collega",
            "baas",
            "project",
            "e-mail",
            "deadline",
            "rapport",
            "kantoor",
          ],
          hebrew: [
            "פגישה",
            "עמית",
            "בוס",
            "פרויקט",
            "אימייל",
            "דדליין",
            'דו"ח',
            "משרד",
          ],
          spanish: [
            "reunión",
            "colega",
            "jefe",
            "proyecto",
            "correo",
            "plazo",
            "informe",
            "oficina",
          ],
          french: [
            "réunion",
            "collègue",
            "patron",
            "projet",
            "email",
            "échéance",
            "rapport",
            "bureau",
          ],
          german: [
            "Besprechung",
            "Kollege",
            "Chef",
            "Projekt",
            "E-Mail",
            "Frist",
            "Bericht",
            "Büro",
          ],
        },
        business: {
          english: [
            "company",
            "client",
            "contract",
            "negotiation",
            "presentation",
            "strategy",
            "profit",
            "market",
          ],
          dutch: [
            "bedrijf",
            "klant",
            "contract",
            "onderhandeling",
            "presentatie",
            "strategie",
            "winst",
            "markt",
          ],
          hebrew: [
            "חברה",
            "לקוח",
            "חוזה",
            "משא ומתן",
            "מצגת",
            "אסטרטגיה",
            "רווח",
            "שוק",
          ],
          spanish: [
            "empresa",
            "cliente",
            "contrato",
            "negociación",
            "presentación",
            "estrategia",
            "beneficio",
            "mercado",
          ],
          french: [
            "entreprise",
            "client",
            "contrat",
            "négociation",
            "présentation",
            "stratégie",
            "profit",
            "marché",
          ],
          german: [
            "Unternehmen",
            "Kunde",
            "Vertrag",
            "Verhandlung",
            "Präsentation",
            "Strategie",
            "Gewinn",
            "Markt",
          ],
        },
        technical: {
          english: [
            "software",
            "computer",
            "website",
            "programming",
            "database",
            "application",
            "algorithm",
            "interface",
          ],
          dutch: [
            "software",
            "computer",
            "website",
            "programmeren",
            "database",
            "applicatie",
            "algoritme",
            "interface",
          ],
          hebrew: [
            "תוכנה",
            "מחשב",
            "אתר",
            "תכנות",
            "מסד נתונים",
            "אפליקציה",
            "אלגוריתם",
            "ממשק",
          ],
          spanish: [
            "software",
            "ordenador",
            "sitio web",
            "programación",
            "base de datos",
            "aplicación",
            "algoritmo",
            "interfaz",
          ],
          french: [
            "logiciel",
            "ordinateur",
            "site web",
            "programmation",
            "base de données",
            "application",
            "algorithme",
            "interface",
          ],
          german: [
            "Software",
            "Computer",
            "Webseite",
            "Programmierung",
            "Datenbank",
            "Anwendung",
            "Algorithmus",
            "Schnittstelle",
          ],
        },
      },
      daily: {
        time: {
          english: [
            "morning",
            "evening",
            "today",
            "yesterday",
            "tomorrow",
            "hour",
            "minute",
            "week",
          ],
          dutch: [
            "ochtend",
            "avond",
            "vandaag",
            "gisteren",
            "morgen",
            "uur",
            "minuut",
            "week",
          ],
          hebrew: ["בוקר", "ערב", "היום", "אתמול", "מחר", "שעה", "דקה", "שבוע"],
          spanish: [
            "mañana",
            "tarde",
            "hoy",
            "ayer",
            "mañana",
            "hora",
            "minuto",
            "semana",
          ],
          french: [
            "matin",
            "soir",
            "aujourd'hui",
            "hier",
            "demain",
            "heure",
            "minute",
            "semaine",
          ],
          german: [
            "Morgen",
            "Abend",
            "heute",
            "gestern",
            "morgen",
            "Stunde",
            "Minute",
            "Woche",
          ],
        },
        home: {
          english: [
            "house",
            "room",
            "kitchen",
            "bathroom",
            "bedroom",
            "living room",
            "garden",
            "door",
          ],
          dutch: [
            "huis",
            "kamer",
            "keuken",
            "badkamer",
            "slaapkamer",
            "woonkamer",
            "tuin",
            "deur",
          ],
          hebrew: [
            "בית",
            "חדר",
            "מטבח",
            "חדר אמבטיה",
            "חדר שינה",
            "סלון",
            "גינה",
            "דלת",
          ],
          spanish: [
            "casa",
            "habitación",
            "cocina",
            "baño",
            "dormitorio",
            "sala de estar",
            "jardín",
            "puerta",
          ],
          french: [
            "maison",
            "pièce",
            "cuisine",
            "salle de bain",
            "chambre",
            "salon",
            "jardin",
            "porte",
          ],
          german: [
            "Haus",
            "Zimmer",
            "Küche",
            "Badezimmer",
            "Schlafzimmer",
            "Wohnzimmer",
            "Garten",
            "Tür",
          ],
        },
        activities: {
          english: [
            "read",
            "write",
            "watch",
            "listen",
            "walk",
            "eat",
            "sleep",
            "talk",
          ],
          dutch: [
            "lezen",
            "schrijven",
            "kijken",
            "luisteren",
            "wandelen",
            "eten",
            "slapen",
            "praten",
          ],
          hebrew: [
            "לקרוא",
            "לכתוב",
            "לצפות",
            "להקשיב",
            "ללכת",
            "לאכול",
            "לישון",
            "לדבר",
          ],
          spanish: [
            "leer",
            "escribir",
            "mirar",
            "escuchar",
            "caminar",
            "comer",
            "dormir",
            "hablar",
          ],
          french: [
            "lire",
            "écrire",
            "regarder",
            "écouter",
            "marcher",
            "manger",
            "dormir",
            "parler",
          ],
          german: [
            "lesen",
            "schreiben",
            "schauen",
            "hören",
            "gehen",
            "essen",
            "schlafen",
            "sprechen",
          ],
        },
      },
    };

    // Detect topics and generate relevant vocabulary in target language
    let suggestedWords: string[] = [];
    let responseContent = "";
    const localizedResponses = getLocalizedResponse("", targetLangDisplay);

    // Enhanced topic detection with input analysis
    const analyzeInput = (input: string) => {
      // Common keywords that indicate different topics
      const keywords = {
        war: [
          "war",
          "battle",
          "military",
          "soldier",
          "army",
          "weapon",
          "oorlog",
          "gevecht",
          "militair",
          "soldaat",
          "leger",
          "wapen",
          "מלחמה",
          "צבא",
          "חייל",
          "קרב",
          "נשק",
        ],
        politics: [
          "politics",
          "government",
          "election",
          "vote",
          "political",
          "democracy",
          "president",
          "parliament",
          "politiek",
          "regering",
          "פוליטיקה",
          "ממשלה",
        ],
        colors: [
          "color",
          "colours",
          "kleur",
          "צבע",
          "color",
          "farbe",
          "red",
          "blue",
          "green",
        ],
        food: [
          "food",
          "eat",
          "eten",
          "אוכל",
          "comida",
          "essen",
          "cook",
          "restaurant",
          "meal",
        ],
        family: [
          "family",
          "familie",
          "משפחה",
          "familia",
          "parent",
          "child",
          "mother",
          "father",
        ],
        travel: [
          "travel",
          "trip",
          "voyage",
          "נסיעה",
          "viaje",
          "reise",
          "flight",
          "hotel",
          "tourist",
        ],
        work: [
          "work",
          "job",
          "office",
          "werk",
          "עבודה",
          "trabajo",
          "arbeit",
          "computer",
          "business",
        ],
        daily: [
          "daily",
          "everyday",
          "routine",
          "dagelijks",
          "יומי",
          "diario",
          "täglich",
          "day",
          "time",
        ],
        technical: [
          "computer",
          "technology",
          "software",
          "programming",
          "code",
          "app",
          "website",
          "technical",
        ],
      };

      // For more specific detection
      const subtypes = {
        war: {
          basic: ["basic", "general", "common"],
          equipment: [
            "equipment",
            "weapon",
            "tank",
            "bomb",
            "rifle",
            "missile",
          ],
          strategy: [
            "strategy",
            "attack",
            "retreat",
            "surrender",
            "victory",
            "defeat",
          ],
        },
        politics: {
          basic: ["basic", "general", "simple", "common"],
          debate: ["debate", "policy", "opinion", "ideology", "reform"],
          international: [
            "international",
            "diplomacy",
            "foreign",
            "global",
            "world",
          ],
        },
        colors: {
          basic: ["basic", "simple", "primary"],
          advanced: ["advanced", "more", "complex", "secondary"],
        },
        food: {
          basic: ["basic", "simple", "general"],
          restaurant: ["restaurant", "dining", "cafe", "menu"],
          cooking: ["cook", "cooking", "recipe", "kitchen", "prepare"],
        },
        // ... rest of subtypes
      };

      // Direct theme detection - prioritize what the user explicitly asks for
      const directThemeMatch = (input: string): string | null => {
        // Dutch specific mappings - map Dutch words to English categories
        const dutchMappings: Record<string, string> = {
          oorlog: "war",
          politiek: "politics",
          kleuren: "colors",
          eten: "food",
          familie: "family",
          reizen: "travel",
          werk: "work",
        };

        // First check for direct Dutch words in the input
        for (const [dutchWord, category] of Object.entries(dutchMappings)) {
          if (input.toLowerCase().includes(dutchWord)) {
            console.log(
              `Dutch word detected: ${dutchWord} -> mapping to ${category}`
            );
            return category;
          }
        }

        const themePatterns = [
          { regex: /\babout\s+(\w+)/i, group: 1 },
          { regex: /\blearn\s+(\w+)/i, group: 1 },
          { regex: /\brelated\s+to\s+(\w+)/i, group: 1 },
          { regex: /\b(\w+)\s+vocabulary\b/i, group: 1 },
          { regex: /\b(\w+)\s+words\b/i, group: 1 },
          { regex: /\bwords\s+(?:about|for|on)\s+(\w+)/i, group: 1 },
          { regex: /\bwoorden\s+(?:over|voor)\s+(\w+)/i, group: 1 }, // Dutch pattern
          { regex: /\bover\s+(\w+)/i, group: 1 }, // Dutch "over X" pattern
        ];

        for (const pattern of themePatterns) {
          const match = input.match(pattern.regex);
          if (match && match[pattern.group]) {
            const theme = match[pattern.group].toLowerCase();
            console.log(`Direct theme match found: ${theme}`);

            // Check if this is a Dutch word that needs mapping
            if (dutchMappings[theme]) {
              console.log(
                `Mapping Dutch theme ${theme} to ${dutchMappings[theme]}`
              );
              return dutchMappings[theme];
            }

            return theme;
          }
        }

        // Last check - look for any Dutch word in the input text
        const inputLower = input.toLowerCase();
        if (
          inputLower.includes("oorlog") ||
          inputLower.includes("militair") ||
          inputLower.includes("soldaat")
        ) {
          console.log("Detected Dutch war-related term");
          return "war";
        }

        return null;
      };

      const directTheme = directThemeMatch(input);

      // Match direct theme to our categories
      if (directTheme) {
        for (const [category, terms] of Object.entries(keywords)) {
          if (terms.includes(directTheme) || category === directTheme) {
            console.log(`Direct category match: ${category}`);
            return {
              matchedCategories: [category],
              matchedSubtypes: { [category]: [] },
              explicitRequest: true,
            };
          }
        }
      }

      // Check which categories match the input
      const matchedCategories: string[] = [];
      for (const [category, terms] of Object.entries(keywords)) {
        for (const term of terms) {
          if (input.toLowerCase().includes(term.toLowerCase())) {
            matchedCategories.push(category);
            break;
          }
        }
      }

      // Track subtypes if we have a category match
      const matchedSubtypes: Record<string, string[]> = {};
      for (const category of matchedCategories) {
        matchedSubtypes[category] = [];

        if (subtypes[category as keyof typeof subtypes]) {
          for (const [subtype, terms] of Object.entries(
            subtypes[category as keyof typeof subtypes]
          )) {
            for (const term of terms) {
              if (input.toLowerCase().includes(term.toLowerCase())) {
                matchedSubtypes[category].push(subtype);
                break;
              }
            }
          }
        }
      }

      console.log("Matched categories:", matchedCategories);
      console.log("Matched subtypes:", matchedSubtypes);

      return {
        matchedCategories,
        matchedSubtypes,
        explicitRequest: !!directTheme,
      };
    };

    // Analyze the user input
    const analysis = analyzeInput(input);

    // Topic detection - with improved matching to avoid returning the same words
    let matchFound = false;

    // First try to match based on analyzed categories
    if (analysis.matchedCategories.length > 0) {
      for (const category of analysis.matchedCategories) {
        // If this is an explicit request, always honor it even if we've processed this category before
        const categoryKey = `${category}-processed`;
        if (!analysis.explicitRequest && processedTopics.has(categoryKey)) {
          console.log(`Skipping already processed category: ${category}`);
          continue;
        }

        // Check if we have subtypes for this category
        let subtypeToUse = "";
        if (category in vocabularyMap) {
          // If we matched subtypes, use the first one
          if (
            analysis.matchedSubtypes[category] &&
            analysis.matchedSubtypes[category].length > 0
          ) {
            subtypeToUse = analysis.matchedSubtypes[category][0];
          } else {
            // Otherwise pick a random subtype
            const availableSubtypes = Object.keys(vocabularyMap[category]);
            subtypeToUse =
              availableSubtypes[
                Math.floor(Math.random() * availableSubtypes.length)
              ];
          }

          // Get words for this category and subtype
          const topicKey = `${category}-${subtypeToUse}`;
          if (analysis.explicitRequest || !processedTopics.has(topicKey)) {
            const words =
              vocabularyMap[category][subtypeToUse][targetLanguage] ||
              vocabularyMap[category][subtypeToUse].english;

            // Filter out words that have already been suggested
            const filteredWords = analysis.explicitRequest
              ? words // If explicit request, use all words regardless of previous use
              : words.filter((word) => !usedWords.has(word));

            if (filteredWords.length > 0) {
              suggestedWords = filteredWords;
              responseContent =
                localizedResponses[category] || localizedResponses.general;

              // Mark this topic as processed
              setProcessedTopics(
                (prev) => new Set([...prev, topicKey, categoryKey])
              );

              // Mark these words as used
              setUsedWords((prev) => new Set([...prev, ...filteredWords]));

              matchFound = true;
              console.log(
                `Matched category: ${category}, subtype: ${subtypeToUse}, suggesting words:`,
                suggestedWords
              );
              break;
            }
          }
        }
      }
    }

    // If no specific match or all matches have been processed, use a random category
    if (!matchFound) {
      // Get all possible category-subtype combinations
      const allCombinations: string[] = [];

      for (const category in vocabularyMap) {
        for (const subtype in vocabularyMap[category]) {
          allCombinations.push(`${category}-${subtype}`);
        }
      }

      // Filter out already processed topics
      const remainingTopics = allCombinations.filter(
        (key) => !processedTopics.has(key)
      );

      if (remainingTopics.length > 0) {
        // Pick a random unprocessed topic
        const randomTopic =
          remainingTopics[Math.floor(Math.random() * remainingTopics.length)];
        const [category, subtype] = randomTopic.split("-");

        const words =
          vocabularyMap[category][subtype][targetLanguage] ||
          vocabularyMap[category][subtype].english;

        // Filter out words that have already been suggested
        const filteredWords = words.filter((word) => !usedWords.has(word));

        if (filteredWords.length > 0) {
          suggestedWords = filteredWords.slice(0, 6); // Limit to 6 words for variety
          responseContent = localizedResponses.general;

          // Mark this topic as processed
          setProcessedTopics((prev) => new Set([...prev, randomTopic]));

          // Mark these words as used
          setUsedWords((prev) => new Set([...prev, ...filteredWords]));
          console.log(
            `Using random topic: ${category}-${subtype}, suggesting words:`,
            suggestedWords
          );
        } else {
          // If all words have been used, reset and start over
          suggestedWords = words.slice(0, 5);
          responseContent = localizedResponses.general;
          setProcessedTopics(new Set());
          setUsedWords(new Set(selectedWords));
          console.log(
            `All words used, resetting and suggesting:`,
            suggestedWords
          );
        }
      } else {
        // If all topics have been processed, reset and start over
        const firstTopic = allCombinations[0];
        const [category, subtype] = firstTopic.split("-");

        suggestedWords =
          vocabularyMap[category][subtype][targetLanguage] ||
          vocabularyMap[category][subtype].english;
        responseContent = localizedResponses.general;

        // Reset processed topics
        setProcessedTopics(new Set([firstTopic]));
        setUsedWords(new Set([...selectedWords, ...suggestedWords]));
        console.log(
          `All topics processed, resetting and suggesting:`,
          suggestedWords
        );
      }
    }

    // Add follow-up questions based on progress
    if (selectedWords.length + (suggestedWords?.length || 0) < 15) {
      responseContent += localizedResponses.progress15;
    } else if (selectedWords.length + (suggestedWords?.length || 0) < 25) {
      responseContent += localizedResponses.progress25;
    } else {
      responseContent += localizedResponses.complete;
      setChatPhase("complete");
    }

    return { content: responseContent, suggestedWords };
  };

  return {
    messages,
    currentMessage,
    setCurrentMessage,
    selectedWords,
    isAgentTyping,
    chatPhase,
    sendMessage,
    addWordToCollection,
  };
};
